import React, { useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { Button } from 'primereact/button';
import utils from '../../../../../utils/utils';
import { user } from '../../../../../redux/user';
import { useDeleteFlexCredentialMutation } from '../../../../../redux/flex-professional-credentials';
import ConfirmationModalNew from '../../../../../components/modals/ConfirmationModalNew';
import LoadingSpinner from '../../../../../components/spinner/LoadingSpinner';
import { showNotification } from '../../../../../redux/notification';
import { PERMISSION as PERMISSIONS } from '../../../../../constants';
import actionsIcon from '../../../../../assets/images/delete-red.svg';

const FlexCredentialsActionDelete = ({
	managementCompanyId,
	caregiverId,
	rowData,
	sameCredentialCount,
	actionIdIdentifier,
	credentialType
}) => {
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [deleteFlexCredential, { isLoading: isDeleting }] = useDeleteFlexCredentialMutation();
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);

	const hasDeletePermission = utils.isAuthorizedExact(
		PERMISSIONS.WORKFORCE_CREDENTIALS_DELETE,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const isPrimaryPL = credentialType === 'PROFESSIONAL_LICENSE' && sameCredentialCount === 1;

	const acceptDeleteAction = async () => {
		const displayedCredentialName =
			rowData.parent_credential_name === rowData.credential_name
				? rowData.credential_name
				: `${rowData.parent_credential_name} - ${
						credentialType === 'SPECIALTY' ? rowData.name : rowData.credential_name || rowData.short_name
				  }`;
		try {
			await deleteFlexCredential({
				managementCompanyId,
				credentialType,
				caregiverId,
				credentialId: rowData[actionIdIdentifier]
			}).unwrap();
			setShowConfirmationModal(false);
			dispatch(showNotification('success', `${displayedCredentialName} credential has been deleted.`));
		} catch (error) {
			if (error?.data?.errors?.[0]?.reason === 'CREDENTIAL_NOT_FOUND') {
				setShowConfirmationModal(false);
				dispatch(showNotification('success', `${displayedCredentialName} credential has been deleted.`));
			} else {
				dispatch(
					showNotification(
						'error',
						`There was an error deleting the ${displayedCredentialName} credential. Please try again.`
					)
				);
			}
		}
	};

	const onDeleteClick = () => setShowConfirmationModal(true);

	// hide the button if the credential does not belong to the management company id;
	const canBeDeleted = rowData.management_company_id === managementCompanyId;
	if (!canBeDeleted) {
		return null;
	}

	const confirmationMessage = isPrimaryPL
		? 'Deleting the primary license cannot be undone. Once deleted, this professional will not be able to be assigned to shifts or claim shifts until a new license is added. Are you sure you want to delete this credential?'
		: 'Deleting a credential cannot be undone. Are you sure you want to delete this credential?';

	return (
		<>
			<Button
				type="button"
				disabled={!hasDeletePermission}
				className={classNames('btn actions')}
				onClick={onDeleteClick}
				aria-controls="popup_menu_left"
				aria-haspopup
			>
				<img src={actionsIcon} alt="upload" />
			</Button>

			{showConfirmationModal && (
				<ConfirmationModalNew
					className={classNames('workforce-confirmation-modal', {
						'is-loading': isDeleting
					})}
					backdropClassName="custom-backdrop"
					size="l"
					onCancel={() => setShowConfirmationModal(false)}
					onConfirm={acceptDeleteAction}
					title={`Delete ${isPrimaryPL ? 'primary license' : ''} credential?`}
					cancelLabel="Keep credential"
					confirmLabel="Delete credential"
				>
					{isDeleting ? <LoadingSpinner /> : confirmationMessage}
				</ConfirmationModalNew>
			)}
		</>
	);
};

export default FlexCredentialsActionDelete;
